import { Controller } from "@hotwired/stimulus"

// 구매 폰트 zip 은 브라우저가 서버에서 직접 받는다(숨은 폼 제출).
//
// 예전에는 axios 로 메모리에 다 받은 뒤 blob URL 로 저장했다. 그러면 서버는 끝까지 보냈는데 브라우저가 파일로
// 저장하다 실패하는 경우("인터넷 연결 상태 확인", 2026-10-25 GPZ비디오테잎 주문)를 서버가 알 수 없어
// 받지 못한 다운로드도 횟수가 깎였다. 직접 받으면 저장 단계가 따로 없고, 도중에 끊기면 서버도 끝나지 않은 것으로 본다.
//
// 진행률은 브라우저 다운로드 표시가 보여준다(서버가 Content-Length 를 보낸다). 끝까지 받으면 서버가 이 화면을
// 다시 읽게 해(broadcast_refresh) 남은 횟수가 바뀐다. 받을 수 없으면 서버가 주문 화면으로 돌려보내 안내를 띄운다.
export default class extends Controller {
  static values = {
    autoDownload: Boolean,
    loadingHtml: String
  }

  static targets = ["button", "form", "autoDownloadField"]

  // 페이지는 다운로드가 언제 끝나는지 알 수 없다. 응답이 오기 전 중복 제출만 막고 버튼을 되돌린다.
  static RESET_DELAY = 5000

  connect() {
    if (this.hasButtonTarget) {
      this.originalButtonHTML = this.buttonTarget.innerHTML
    }

    this.isDownloading = false

    // 최초 접근 시 자동 다운로드. 폼 제출은 페이지 이동이라, 페이지가 다 뜨기 전에 보내면 load 가 끝나지 않는다.
    if (this.autoDownloadValue) {
      if (document.readyState === "complete") {
        this.submit(true)
      } else {
        this.autoDownloadOnLoad = () => this.submit(true)
        window.addEventListener("load", this.autoDownloadOnLoad, { once: true })
      }
    }
  }

  disconnect() {
    window.removeEventListener("load", this.autoDownloadOnLoad)
    clearTimeout(this.resetTimer)
    delete document.documentElement.dataset.fileDownloadPending
  }

  downloadFile(_event) {
    this.submit(false)
  }

  // 서버는 auto_download=true 이고 이미 받은 주문이면 204 로 무시한다(브라우저는 화면을 그대로 둔다).
  submit(isAutoDownload) {
    if (this.isDownloading || !this.hasFormTarget) return

    this.isDownloading = true
    this.setButtonLoading(true)

    // 폼 제출은 응답이 다운로드여도 beforeunload 를 부른다. Turbo 를 끄지 말라고 알린다(application_with_turbo.js).
    document.documentElement.dataset.fileDownloadPending = "true"
    this.autoDownloadFieldTarget.value = isAutoDownload ? "true" : ""
    this.formTarget.requestSubmit()

    this.resetTimer = setTimeout(() => {
      this.isDownloading = false
      this.setButtonLoading(false)
      delete document.documentElement.dataset.fileDownloadPending
    }, this.constructor.RESET_DELAY)
  }

  setButtonLoading(loading) {
    if (!this.hasButtonTarget) return

    this.buttonTarget.disabled = loading

    if (loading) {
      this.buttonTarget.classList.add("opacity-50", "cursor-not-allowed")
      this.buttonTarget.innerHTML = this.loadingHtmlValue
    } else {
      this.buttonTarget.classList.remove("opacity-50", "cursor-not-allowed")
      this.buttonTarget.innerHTML = this.originalButtonHTML
    }
  }

  confirmDownload(event) {
    event.preventDefault()

    if (this.isDownloading) return

    const confirmed = confirm(
      "모바일 또는 웹뷰 환경에서 다운로드 시 파일이 정상적으로 저장되지 않을 수 있으며, 이 경우 다운로드 횟수 복원이 불가능합니다. 그래도 다운로드 하시겠습니까?"
    )

    if (confirmed) {
      this.downloadFile(event)
    }
  }
}
